import { createListenerMiddleware, type UnknownAction } from '@reduxjs/toolkit'
import _, { debounce } from 'lodash'
import { createEventBus } from '#utils/event'
import { log, logger } from '#utils/logger'

export type ReduxVersion = string & { __reduxVersionBrand: never }

export interface StoredState<TType extends string> {
  __version: ReduxVersion
  __updatedAt: Timestamp
  __type: TType
  __maicVersion: string
}

export interface LocalStorageConfiguration<TReduxState extends object, TType extends string> {
  readonly db: string
  readonly key: string
  readonly version: ReduxVersion
  readonly type: TType
  readonly parseState?: (parsedJson: Partial<TReduxState>, version: ReduxVersion, maicVersion: string) => Partial<TReduxState>
  readonly makeStoredState?: (clonedState: TReduxState) => Partial<TReduxState>
  shouldSave(action: UnknownAction): boolean
}

export function isStoredStateOfType<TType extends string>(state: unknown, type: TType): state is StoredState<TType> {
  if (typeof state !== 'object' || state === null) {
    return false
  }
  if (!('__type' in state)) {
    return false
  }

  return state.__type === type
}

const localStorageLogger = logger('🗃️ Local Storage:')
const storageVersion = 2

export const storageEventBus = createEventBus<{
  save: () => void
}>()

const initializeDatabase = async (dbName: string, initialize: (db: IDBDatabase) => void) => {
  const request = window.indexedDB.open(dbName, storageVersion)
  return new Promise<IDBDatabase>((resolve, reject) => {
    request.addEventListener('blocked', () => {
      localStorageLogger.error('IndexDB access was denied, skipping')
      reject()
    })
    request.addEventListener('error', (ev) => {
      localStorageLogger.error('Error opening IndexedDB:', ev)
      reject()
    })
    request.addEventListener('upgradeneeded', () => {
      initialize(request.result)
    })
    request.addEventListener('success', () => {
      resolve(request.result)
    })
  })
}

const loadStoredState = <TReduxState extends object>(db: IDBDatabase, key: string, type: string) => {
  const transaction = db.transaction(key, 'readonly')
  const store = transaction.objectStore(key)
  const request = store.get(type)
  return new Promise<TReduxState | undefined>((resolve, _reject) => {
    request.addEventListener('error', (ev) => {
      localStorageLogger.error('Error loading initial state', ev)
      resolve(undefined)
    })
    request.addEventListener('success', () => resolve(request.result as TReduxState))
  })
}

export async function buildLocalStorageMiddleware<TReduxState extends object, TType extends string>(
  config: LocalStorageConfiguration<TReduxState, TType>,
) {
  type StoredRootState = StoredState<TType> & Partial<TReduxState>
  const localStorageListener = createListenerMiddleware<TReduxState>()

  const db = await initializeDatabase(config.db, (db) => {
    if (!db.objectStoreNames.contains(config.key)) {
      // enforcing to clean up old table names. This will cause problems if there are more than the root storage.
      for (const name of db.objectStoreNames) {
        db.deleteObjectStore(name)
      }
      const store = db.createObjectStore(config.key, { keyPath: '__type' })
      store.add({ __type: config.type })
    }
  })

  const debouncedSave = debounce((storedState: StoredRootState) => {
    const transaction = db.transaction(config.key, 'readwrite')
    const storage = transaction.objectStore(config.key)
    storage.put(storedState)
    transaction.addEventListener('error', (evt) => localStorageLogger.error(evt))
    transaction.addEventListener('complete', () => localStorageLogger.debug('Saving to local storage'))
    transaction.commit()
    storageEventBus.emit('save')
    window.dispatchEvent(new CustomEvent('localstorage-save'))
  }, 500)

  function makeStoredState(rootState: TReduxState): StoredRootState {
    const metaData: StoredState<TType> = {
      __version: config.version,
      __updatedAt: Date.now(),
      __type: config.type,
      __maicVersion: `v${VITE_APP_VERSION} (${VITE_COMMIT_SHA})`,
    }

    const clonedState = structuredClone({ ...rootState, tmsApi: undefined })
    return {
      ...metaData,
      ...(config.makeStoredState ? config.makeStoredState(clonedState) : clonedState),
    }
  }

  localStorageListener.startListening({
    // listen to all actions that change the localConfig or userData slice
    predicate: (action) => config.shouldSave(action),
    effect: (_action, api) => {
      const storedState = makeStoredState(api.getState())
      debouncedSave(storedState)
    },
  })

  const internalLoadStoredState = async (initialState: TReduxState): Promise<TReduxState | undefined> => {
    const initState = await loadStoredState<TReduxState>(db, config.key, config.type)

    if (!isStoredStateOfType(initState, config.type)) {
      return undefined
    }

    // ignore the non redux keys, redux does not expect them
    const { __version, __maicVersion, __type, __updatedAt, ...rest } = initState

    const serializedVersion = __version
    if (serializedVersion !== config.version) {
      log.warn(`Tossing serialized state, version mismatch: Expected '${config.version}', received '${serializedVersion}'`)
      localStorage.removeItem(config.key)
      return undefined
    }

    const preloadedState = config.parseState ? config.parseState(rest as unknown as Partial<TReduxState>, __version, __maicVersion) : rest
    // take the initial state of all reducers and let the preloaded state overwrite each property
    // the empty object is required, as `initialState` is readonly
    return _.mergeWith({}, initialState, preloadedState) satisfies TReduxState
  }

  return {
    loadStoredState: internalLoadStoredState,
    localStorageListener,
  }
}
